<template>
  <div>
    <!-- Button to open the modal -->
    <button
      @click="openModal"
      class="btn-secondary-sm"
    >
      Manage Entity Parents
    </button>

    <!-- Modal backdrop -->
    <div
      v-if="isModalOpen"
      class="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      @click.self="closeModal"
    >
      <!-- Modal content -->
      <div class="bg-white rounded-lg shadow-xl w-full mx-48 overflow-hidden text-sm">
        <div class="flex justify-between items-center border-b px-6 py-3">
          <h3 class="text-base font-semibold">Manage Entity Parents</h3>
          <button @click="closeModal" class="text-gray-500 hover:text-gray-700">
            <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"></path>
            </svg>
          </button>
        </div>

        <div class="px-6 py-4 max-h-[70vh] overflow-y-auto">
          <!-- Current parent entities -->
          <div v-if="currentParents.length > 0" class="mb-5">
            <h4 class="text-sm font-medium mb-2">Current Parent Entities</h4>
            <div class="bg-gray-50 rounded border overflow-hidden">
              <div class="grid grid-cols-12 py-2 px-3 bg-gray-100 font-medium text-xs">
                <span class="col-span-4">Type</span>
                <span class="col-span-6">Name</span>
                <span class="col-span-2"></span>
              </div>
              <div v-for="parent in currentParents" :key="parent.id" class="grid grid-cols-12 py-2 px-3 border-t border-gray-200 items-center">
                <span class="col-span-4 truncate text-gray-800">{{ parent.entity_type_name }}</span>
                <span class="col-span-6 truncate text-gray-800">{{ parent.name }}</span>
                <div class="col-span-2 text-right">
                  <button
                    type="button"
                    class="btn-danger-sm"
                    @click="removeParent(parent.id)"
                    :disabled="processing"
                  >
                    Remove
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="mb-5">
            <p class="text-gray-500 italic text-xs">No parent entities</p>
          </div>

          <!-- Available entities to set as parents -->
          <div class="mb-5">
            <h4 class="text-sm font-medium mb-2">Add Parent Entity</h4>
            <form @submit.prevent="submitForm" class="bg-gray-50 rounded border p-3">
              <!-- Entity Type Filter -->
              <div class="mb-3">
                <label class="block text-xs text-gray-700 mb-1">Filter by Entity Type</label>
                <select
                  v-model="selectedEntityType"
                  class="w-full p-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-dark focus:border-blue-500 transition-colors"
                  :disabled="processing"
                >
                  <option value="">All Entity Types</option>
                  <option
                    v-for="type in availableEntityTypes"
                    :key="type.id"
                    :value="type.id"
                  >
                    {{ type.type_name }}
                  </option>
                </select>
              </div>

              <!-- Entity Selection -->
              <div class="mb-3">
                <label class="block text-xs text-gray-700 mb-1">Select Parent Entity</label>
                <select
                  v-model="selectedEntity"
                  class="w-full p-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-dark focus:border-blue-500 transition-colors"
                  :disabled="processing || filteredAvailableEntities.length === 0"
                >
                  <option value="">{{ selectedEntityType ? 'Select an entity...' : 'Select entity type first...' }}</option>
                  <option
                    v-for="entity in filteredAvailableEntities"
                    :key="entity.id"
                    :value="entity.id"
                  >
                    {{ entity.name }}
                  </option>
                </select>
              </div>

              <button
                type="submit"
                class="btn-primary-sm"
                :disabled="!selectedEntity || processing"
              >
                Add Parent
              </button>
            </form>
          </div>

          <!-- Status messages -->
          <div v-if="message" class="p-2 rounded text-xs" :class="messageType === 'alert-success' ? 'bg-green-100 text-green-800' : messageType === 'alert-danger' ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'">
            {{ message }}
          </div>
        </div>

        <div class="border-t px-6 py-3 flex justify-end">
          <button
            @click="closeModal"
            class="btn-secondary"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'FormManageGroupParticipation',

  emits: ['updated'],

  props: {
    entityId: {
      type: Number,
      required: true
    }
  },

  data() {
    return {
      isModalOpen: false,
      currentParents: [],
      availableEntities: [],
      availableEntityTypes: [],
      selectedEntity: '',
      selectedEntityType: '',
      processing: false,
      message: '',
      messageType: 'alert-info',
      dataLoaded: false,
      hasChanges: false
    };
  },

  computed: {
    availableEntitiesFiltered() {
      const currentParentIds = this.currentParents.map(parent => parent.id);
      // Filter out current parents and self
      return this.availableEntities.filter(entity =>
        !currentParentIds.includes(entity.id) && entity.id !== this.entityId
      );
    },

    filteredAvailableEntities() {
      let entities = this.availableEntitiesFiltered;

      if (this.selectedEntityType) {
        entities = entities.filter(entity =>
          entity.entity_type === parseInt(this.selectedEntityType)
        );
      }

      return entities;
    }
  },

  methods: {
    openModal() {
      this.isModalOpen = true;
      document.body.style.overflow = 'hidden';
      this.selectedEntityType = '';
      this.selectedEntity = '';

      if (!this.dataLoaded) {
        this.fetchData();
      }
    },

    closeModal() {
      this.isModalOpen = false;
      document.body.style.overflow = '';
      this.message = '';
      if (this.hasChanges) {
        this.$emit('updated');
        this.hasChanges = false;
      }
    },

    async fetchData() {
      this.processing = true;
      try {
        // Fetch current parent entities
        const parentsResponse = await axios.get(`/api/entities/${this.entityId}/parents/`);
        this.currentParents = parentsResponse.data;

        // Fetch entity types for filtering
        const typesResponse = await axios.get('/api/entity-types/');
        this.availableEntityTypes = typesResponse.data;

        this.dataLoaded = true;
      } catch (error) {
        this.showMessage('Error fetching data: ' + (error.response?.data?.detail || error.message), 'alert-danger');
      } finally {
        this.processing = false;
      }
    },

    async submitForm() {
      if (!this.selectedEntity) return;

      this.processing = true;

      try {
        await axios.post(`/api/entities/${this.entityId}/add-parent/`, {
          parent_id: this.selectedEntity
        });

        this.showMessage('Successfully added parent entity', 'alert-success');
        this.selectedEntity = '';

        // Refresh parents list
        const parentsResponse = await axios.get(`/api/entities/${this.entityId}/parents/`);
        this.currentParents = parentsResponse.data;
        this.hasChanges = true;
      } catch (error) {
        if (error.response?.data?.error && error.response.data.error.includes('cycle')) {
          this.showMessage('Cannot add parent: would create cycle in hierarchy', 'alert-danger');
        } else {
          this.showMessage('Error adding parent: ' + (error.response?.data?.error || error.message), 'alert-danger');
        }
      } finally {
        this.processing = false;
      }
    },

    async removeParent(parentId) {
      this.processing = true;
      try {
        await axios.post(`/api/entities/${this.entityId}/remove-parent/`, {
          parent_id: parentId
        });

        this.showMessage('Successfully removed parent entity', 'alert-success');

        // Refresh parents list
        const parentsResponse = await axios.get(`/api/entities/${this.entityId}/parents/`);
        this.currentParents = parentsResponse.data;
        this.hasChanges = true;
      } catch (error) {
        this.showMessage('Error removing parent: ' + (error.response?.data?.error || error.message), 'alert-danger');
      } finally {
        this.processing = false;
      }
    },

    showMessage(text, type = 'alert-info') {
      this.message = text;
      this.messageType = type;

      setTimeout(() => {
        this.message = '';
      }, 5000);
    }
  },

  watch: {
    async selectedEntityType(newValue) {
      this.selectedEntity = '';
      this.availableEntities = [];

      if (newValue) {
        this.processing = true;
        try {
          // Fetch entities filtered by selected type
          const response = await axios.get('/api/entities/', {
            params: { entity_type_id: newValue }
          });
          this.availableEntities = response.data;
        } catch (error) {
          this.showMessage('Error fetching entities: ' + (error.response?.data?.detail || error.message), 'alert-danger');
        } finally {
          this.processing = false;
        }
      }
    }
  }
};
</script>
